import NiceModal, { useModal } from '@ebay/nice-modal-react'
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import { CompanyUserWithUserAndRole } from '@shared/types/companies.types'
import { Role } from '@shared/types/roles.types'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useUpdateCompanyUserMutation } from '../../hooks/useMutations/useCompaniesMutations'
import { useSnackbar } from '../../hooks/useSnackbar'
import { isDefaultRole } from '../../utils/user.utils'
import { LoaderButton } from '../LoaderButton'

export const AssignRoleModal = NiceModal.create(
  ({
    users,
    role,
    companyId
  }: {
    users: CompanyUserWithUserAndRole[]
    role: Role
    companyId: string
  }) => {
    const modal = useModal()
    const { t } = useTranslation()
    const [userId, setUserId] = useState('')
    const [lastFullAcessError, setLastFullAcessError] = useState(false)
    const { showSnackbar } = useSnackbar()
    const mutation = useUpdateCompanyUserMutation(companyId)

    const roleName = isDefaultRole(role.name)
      ? t(`role.${role.name}`)
      : role.name

    const handleSave = () => {
      mutation.mutate(
        { userId, data: { roleId: role.id } },
        {
          onSuccess: () => {
            showSnackbar({
              message: t('assign_role_modal.success', { role: roleName }),
              severity: 'success'
            })
          },
          onError: () => {
            showSnackbar({
              message: t('assign_role_modal.error'),
              severity: 'error'
            })
          },
          onSettled: () => {
            modal.remove()
          }
        }
      )
    }

    const filteredUsers = useMemo(() => {
      return users?.filter(user => user.role.name !== role.name) ?? []
    }, [users, role.name])

    const fullAccessUsers = useMemo(() => {
      return users?.filter(user => user.role.fullAccess) ?? []
    }, [users])

    useEffect(() => {
      setLastFullAcessError(false)

      if (userId) {
        const isCurrentUserLastFullAccess =
          fullAccessUsers.length === 1 && fullAccessUsers[0].userId === userId

        if (!role.fullAccess && isCurrentUserLastFullAccess) {
          setLastFullAcessError(true)
        }
      }
    }, [userId, fullAccessUsers, role])

    return (
      <Dialog
        open={modal.visible}
        onClose={() => modal.remove()}
        sx={{
          '& .MuiDialog-paper': {
            width: '32.5625rem',
            padding: '1.5rem',
            borderRadius: '.25rem'
          }
        }}
      >
        <DialogTitle sx={{ py: '1.5rem' }}>
          <Typography variant='h6' component='span'>
            {t('assign_role_modal.title', {
              role: roleName
            })}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ padding: '1.5rem' }}>
          <Stack direction='column' spacing='1.5rem'>
            <Typography variant='body1'>
              {t('assign_role_modal.description', {
                role: roleName
              })}
            </Typography>
            <FormControl fullWidth>
              <Autocomplete
                options={filteredUsers}
                getOptionLabel={option =>
                  option.user.name && option.user.lastName
                    ? `${option.user.name} ${option.user.lastName}`
                    : ' '
                }
                value={
                  filteredUsers.find(user => user.userId === userId) || null
                }
                noOptionsText={t('assign_role_modal.no_options')}
                renderInput={params => (
                  <TextField
                    {...params}
                    label={t('assign_role_modal.label')}
                    placeholder={t('assign_role_modal.label')}
                  />
                )}
                onChange={(_event, value) => {
                  setUserId(value?.userId ?? '')
                }}
                isOptionEqualToValue={(option, value) =>
                  option.userId === value.userId
                }
              />
            </FormControl>
            {lastFullAcessError && (
              <Alert
                severity='error'
                sx={{
                  mt: '1.5rem',
                  color: 'rgba(95, 33, 32, 1)',
                  backgroundColor: '#FDEDED'
                }}
              >
                <AlertTitle>
                  {t('assign_role_modal.alerts.full_access.title')}
                </AlertTitle>
                {t('assign_role_modal.alerts.full_access.description')}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant='outlined'
            color='primary'
            onClick={() => modal.remove()}
            sx={{
              textTransform: 'none',
              width: '14rem',
              padding: '.5rem 1.375rem',
              backgroundColor: 'rgba(178, 223, 219, 0.10)'
            }}
          >
            {t('common.cancel')}
          </Button>
          <LoaderButton
            onClick={handleSave}
            loading={mutation.isPending}
            disabled={!userId || mutation.isPending || lastFullAcessError}
            variant='contained'
            label={t('common.save')}
            sx={{
              textTransform: 'none',
              width: '14rem',
              padding: '.5rem 1.375rem'
            }}
          />
        </DialogActions>
      </Dialog>
    )
  }
)
